import React, { Component, ReactNode } from "react";
import { captureException } from "@/lib/sentry";
import { Card, CardContent } from "@/components/ui/card";
import { RefreshCw, AlertCircle, Home, Loader2 } from "lucide-react";
import { PrimaryButton, GhostButton } from "@/components/StandardButton";

const isDev = import.meta.env.DEV;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  retryKey: number;
  isRetrying: boolean;
  retryCount: number;
}

/**
 * Checks if error is a chunk/module loading failure
 */
function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("Loading chunk") ||
    error.message.includes("ChunkLoadError") ||
    error.name === "ChunkLoadError"
  );
}

/**
 * Error boundary that catches lazy loading failures.
 * Uses lazyWithRetry for automatic retries, so this boundary
 * only shows when all retries have failed.
 * Dev-mode aware: auto-retries on Vite reconnection.
 */
export class LazyLoadErrorBoundary extends Component<Props, State> {
  private unsubscribeReconnect?: () => void;

  constructor(props: Props) {
    super(props);
    this.state = { 
      hasError: false, 
      retryKey: 0, 
      isRetrying: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error, isRetrying: false };
  }

  componentDidMount() {
    // In dev mode, listen for Vite reconnection to auto-retry
    if (isDev) {
      this.setupReconnectListener();
    }
  }

  componentWillUnmount() {
    this.unsubscribeReconnect?.();
  }

  setupReconnectListener() {
    const handler = () => {
      if (this.state.hasError && this.state.retryCount < 3) {
        console.log('[LazyBoundary] Vite reconnected, auto-retrying...');
        this.handleRetry();
      }
    };
    
    window.addEventListener('vite:reconnected', handler);
    this.unsubscribeReconnect = () => window.removeEventListener('vite:reconnected', handler);
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const isChunkError = isChunkLoadError(error);

    // Only report to Sentry in production
    if (!isDev) {
      captureException(error, {
        context: "lazy_load_error_boundary",
        errorType: isChunkError ? "chunk_load_failure" : "component_error",
        componentStack: errorInfo.componentStack,
      });
    } else {
      console.error('[LazyBoundary] Dev mode error:', error.message);
    }
  }

  handleRetry = () => {
    this.setState(prev => ({ 
      hasError: false, 
      error: undefined, 
      retryKey: prev.retryKey + 1,
      isRetrying: true,
      retryCount: prev.retryCount + 1
    }));
    
    // Reset isRetrying after a short delay
    setTimeout(() => {
      this.setState({ isRetrying: false });
    }, 100);
  };

  handleGoHome = () => {
    window.location.href = "/dashboard";
  };

  handleHardRefresh = () => {
    // Clear caches and do a full reload
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => caches.delete(name));
      });
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError = this.state.error && isChunkLoadError(this.state.error);
      const { retryCount } = this.state;

      return (
        <div className="min-h-[400px] flex items-center justify-center p-4">
          <Card className="max-w-md w-full shadow-lg">
            <CardContent className="pt-6 text-center space-y-4">
              <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                {this.state.isRetrying ? (
                  <Loader2 className="h-6 w-6 text-muted-foreground animate-spin" />
                ) : (
                  <AlertCircle className="h-6 w-6 text-destructive" />
                )}
              </div>
              
              <div className="space-y-2">
                <h2 className="text-lg font-semibold text-foreground">
                  {isDev && isChunkError 
                    ? "Dev server connection issue" 
                    : isChunkError 
                      ? "Page failed to load" 
                      : "Something went wrong"}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {isDev && isChunkError 
                    ? "The dev server may be restarting. This usually resolves automatically."
                    : isChunkError 
                      ? "This can happen due to a network issue or an app update."
                      : "An unexpected error occurred."
                  }
                </p>
                {isDev && retryCount > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Retry attempts: {retryCount}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 pt-2">
                <PrimaryButton 
                  onClick={this.handleRetry}
                  className="w-full"
                  disabled={this.state.isRetrying}
                >
                  {this.state.isRetrying ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {this.state.isRetrying ? "Retrying..." : "Try Again"}
                </PrimaryButton>
                
                {retryCount >= 2 && (
                  <GhostButton 
                    onClick={this.handleHardRefresh}
                    className="w-full"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Hard Refresh
                  </GhostButton>
                )}
                
                <GhostButton 
                  onClick={this.handleGoHome}
                  className="w-full"
                >
                  <Home className="h-4 w-4 mr-2" />
                  Go to Dashboard
                </GhostButton>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Use key to force re-mount when retrying
    return <React.Fragment key={this.state.retryKey}>{this.props.children}</React.Fragment>;
  }
}
